// <sm-cart-drawer-offer>: right drawer, opens on sm:cart:open (cart icon) and sm:cart:added
// (after a successful ATC, once the upsell modal has decided, see sm:upsell:done), and
// re-renders itself via the Section Rendering API response cartAdd/cartChange already
// requested (sm-core.js sections param). HANDOFF 7.7.
import { on, emit, trapFocus, lockScroll } from './sm-core.js';

const SECTION_ID = 'sm-cart-drawer-offer';

class SmCartDrawerOffer extends HTMLElement {
  connectedCallback() {
    this.dialogEl = this; // the drawer element itself acts as the panel
    this.pendingOpen = false;

    this.offOpen = on('sm:cart:open', () => this.open());
    this.offAdded = on('sm:cart:added', (e) => this.onCartAdded(e.detail));
    this.offUpdated = on('sm:cart:updated', (e) => this.onCartUpdated(e.detail));
    this.offUpsellDone = on('sm:upsell:done', () => this.open());

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
      const removeBtn = e.target.closest('[data-line-remove]');
      if (removeBtn) this.removeLine(removeBtn.dataset.variantId);
    });
    // cross-sell rows add to the cart without leaving the page
    this.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-drawer-add]');
      if (!form) return;
      e.preventDefault();
      const root = window.Shopify?.routes?.root || '/';
      await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      await this.refresh();
      emit('sm:cart:updated', {});
    });
    this.onKeydown = (e) => {
      if (e.key === 'Escape') this.close();
    };
  }

  disconnectedCallback() {
    this.offOpen?.();
    this.offAdded?.();
    this.offUpdated?.();
    this.offUpsellDone?.();
  }

  onCartAdded(data) {
    this.updateFromSections(data?.sections);
    // Wait for the upsell modal's decision (sm:upsell:done) before opening, unless it
    // never fires (product has no upgrade stack / not subscribed): open on a short delay.
    // David 29.9.2026: open the cart immediately after every add to cart (desktop and mobile)
    clearTimeout(this._openTimer);
    this.open();
  }

  onCartUpdated(data) {
    this.updateFromSections(data?.sections);
  }

  // Re-render with the section's real id (its section-group instance). Rendering by type
  // ('sm-cart-drawer-offer') drops the group's blocks: no gift ladder tiers, no cross-sell.
  async refresh() {
    const id = this.dataset.sectionId;
    if (!id) return;
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}?section_id=${encodeURIComponent(id)}`);
    this.replaceInner(await res.text());
  }

  updateFromSections() {
    this.refresh().catch(() => {});
  }

  replaceInner(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`[data-section="${SECTION_ID}"]`);
    const current = this.querySelector(`[data-section="${SECTION_ID}"]`);
    if (fresh && current) current.replaceWith(fresh);
  }

  async removeLine(variantId) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      const data = await res.json();
      await this.refresh();
      emit('sm:cart:updated', data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('sm-cart-drawer-offer: remove failed', err);
    }
  }

  open() {
    if (this.hasAttribute('data-open')) return;
    this.setAttribute('data-open', '');
    document.documentElement.setAttribute('data-cart-open', '');
    lockScroll(true);
    document.documentElement.classList.add('sm-drawer-open');
    this.releaseFocus = trapFocus(this);
    document.addEventListener('keydown', this.onKeydown);
  }

  close() {
    if (!this.hasAttribute('data-open')) return;
    document.removeEventListener('keydown', this.onKeydown);
    this.removeAttribute('data-open');
    document.documentElement.removeAttribute('data-cart-open');
    lockScroll(false);
    document.documentElement.classList.remove('sm-drawer-open');
    this.releaseFocus?.();
  }
}
if (!customElements.get('sm-cart-drawer-offer')) customElements.define('sm-cart-drawer-offer', SmCartDrawerOffer);
